import { XCircle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, Legend } from 'recharts';

type ParameterModalProps = {
  isOpen: boolean;
  onClose: () => void;
  parameter: {
    label: string;
    value: string;
    description: string;
    chartData?: Array<{ time: string; value: number }>;
  } | null;
};

export default function ParameterModal({ isOpen, onClose, parameter }: ParameterModalProps) {
  if (!isOpen || !parameter) return null;

  // Get unit string from parameter value if available
  const getUnit = (): string => {
    if (!parameter.value) return '';
    
    if (parameter.value.includes('°C')) return '°C';
    if (parameter.value.includes('PSU')) return 'PSU';
    if (parameter.value.includes('NTU')) return 'NTU';
    if (parameter.value.includes('mg/L')) return 'mg/L';
    if (parameter.label === 'pH') return '';
    return '';
  };

  const unit = getUnit();

  const renderChart = () => {
    if (!parameter.chartData || parameter.chartData.length === 0) {
      return (
        <div className="flex items-center justify-center h-full">
          <p className="text-[#5D5346]">No historical data available</p>
        </div>
      );
    }

    switch (parameter.label) {
      case 'TEMPERATURE':
        return (
          <LineChart data={parameter.chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <XAxis dataKey="time" stroke="#5D5346" fontSize={12} tickLine={false} />
            <YAxis stroke="#5D5346" fontSize={12} tickLine={false} axisLine={false} 
                   tickFormatter={(value) => `${value}°C`} />
            <Tooltip 
              contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', border: 'none', borderRadius: '4px' }}
              formatter={(value) => [`${value}°C`, 'Temperature']}
            />
            <Legend />
            <Line 
              name="Temperature" 
              type="monotone" 
              dataKey="value" 
              stroke="#005E6E" 
              strokeWidth={2} 
              dot={{ fill: '#005E6E', r: 4 }}
              activeDot={{ r: 6, stroke: '#005E6E', strokeWidth: 2 }}
            />
          </LineChart>
        );
      
      case 'SALINITY':
        return (
          <AreaChart data={parameter.chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <XAxis dataKey="time" stroke="#5D5346" fontSize={12} tickLine={false} />
            <YAxis stroke="#5D5346" fontSize={12} tickLine={false} axisLine={false}
                   tickFormatter={(value) => `${value} PSU`} />
            <Tooltip 
              contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', border: 'none', borderRadius: '4px' }}
              formatter={(value) => [`${value} PSU`, 'Salinity']}
            />
            <Legend />
            <Area 
              name="Salinity" 
              type="monotone" 
              dataKey="value" 
              fill="#005E6E" 
              fillOpacity={0.2} 
              stroke="#005E6E"
              activeDot={{ r: 6, stroke: '#005E6E', strokeWidth: 2 }}
            />
          </AreaChart>
        );

      case 'TURBIDITY':
        return (
          <BarChart data={parameter.chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <XAxis dataKey="time" stroke="#5D5346" fontSize={12} tickLine={false} />
            <YAxis stroke="#5D5346" fontSize={12} tickLine={false} axisLine={false}
                   tickFormatter={(value) => `${value} NTU`} />
            <Tooltip 
              contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', border: 'none', borderRadius: '4px' }}
              formatter={(value) => [`${value} NTU`, 'Turbidity']}
            />
            <Legend />
            <Bar 
              name="Turbidity" 
              dataKey="value" 
              fill="#005E6E" 
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        );

      case 'pH':
        return (
          <LineChart data={parameter.chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <XAxis dataKey="time" stroke="#5D5346" fontSize={12} tickLine={false} />
            <YAxis stroke="#5D5346" fontSize={12} tickLine={false} axisLine={false} />
            <Tooltip 
              contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', border: 'none', borderRadius: '4px' }}
              formatter={(value) => [value, 'pH']}
            />
            <Legend />
            <Line 
              name="pH" 
              type="monotone" 
              dataKey="value" 
              stroke="#005E6E" 
              strokeWidth={2} 
              dot={{ fill: '#005E6E', r: 4 }}
              activeDot={{ r: 6, stroke: '#005E6E', strokeWidth: 2 }}
            />
          </LineChart>
        );

      case 'DISSOLVED OXYGEN':
        return (
          <LineChart data={parameter.chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <XAxis dataKey="time" stroke="#5D5346" fontSize={12} tickLine={false} />
            <YAxis stroke="#5D5346" fontSize={12} tickLine={false} axisLine={false} 
                   tickFormatter={(value) => `${value} mg/L`} />
            <Tooltip 
              contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', border: 'none', borderRadius: '4px' }}
              formatter={(value) => [`${value} mg/L`, 'Dissolved Oxygen']}
            />
            <Legend />
            <Line 
              name="Dissolved Oxygen" 
              type="monotone" 
              dataKey="value" 
              stroke="#005E6E" 
              strokeWidth={2} 
              dot={{ fill: '#005E6E', r: 4 }}
              activeDot={{ r: 6, stroke: '#005E6E', strokeWidth: 2 }}
            />
          </LineChart>
        );

      default:
        return (
          <LineChart data={parameter.chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <XAxis dataKey="time" stroke="#5D5346" fontSize={12} tickLine={false} />
            <YAxis stroke="#5D5346" fontSize={12} tickLine={false} axisLine={false} />
            <Tooltip 
              contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', border: 'none', borderRadius: '4px' }}
            />
            <Legend />
            <Line 
              name={parameter.label} 
              type="monotone" 
              dataKey="value" 
              stroke="#005E6E" 
              strokeWidth={2} 
              dot={{ fill: '#005E6E', r: 4 }}
              activeDot={{ r: 6, stroke: '#005E6E', strokeWidth: 2 }}
            />
          </LineChart>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[2000] flex items-center justify-center">
      <div className="bg-white rounded-xl w-[800px] max-w-[90vw] max-h-[90vh] overflow-auto shadow-xl">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-[#005E6E]">{parameter.label}</h3>
            <p className="text-sm text-[#5D5346]">{parameter.description}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XCircle className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-6">
          <div className="mb-8 flex items-baseline">
            <p className="text-3xl font-bold text-[#005E6E]">{parameter.value}</p>
            {unit && <p className="text-sm ml-2 text-[#5D5346]">Current reading</p>}
          </div>

          {parameter.chartData && parameter.chartData.length > 0 ? (
            <>
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-[#005E6E]">Historical Data</h4>
                <p className="text-sm text-[#5D5346]">
                  Showing {parameter.chartData.length} recent readings
                </p>
              </div>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  {renderChart()}
                </ResponsiveContainer>
              </div>
              <div className="mt-4 text-xs text-[#5D5346] text-right">
                Data updates every 30 seconds
              </div>
            </>
          ) : (
            <div className="py-10 text-center">
              <p className="text-[#5D5346]">No historical data available for this parameter</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 